import { useForm, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import { AlertCircle, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { SearchableSelect } from "@/components/SearchableSelect"
import type { SelectOption, MoveLocationFormData } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"

interface MoveLocationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  assetType: AssetType
  selectedIds: Set<string>
  allCodes: string[]
  locations: SelectOption[]
}

export function MoveLocationDialog({
  open,
  onOpenChange,
  onSuccess,
  assetType,
  selectedIds,
  allCodes,
  locations,
}: MoveLocationDialogProps) {
  const { t } = useTranslation()
  const bulkCount = selectedIds.size

  const form = useForm<MoveLocationFormData>({
    location_id: "",
    notes: "",
  })

  useEffect(() => {
    if (open) {
      form.clearErrors()
      form.setData({ location_id: "", notes: "" })
    }
  }, [open])

  const hasErrors = Object.keys(form.errors).length > 0
  const errorMessages = Object.values(form.errors).filter(Boolean) as string[]

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.clearErrors()

    const assetIds = Array.from(selectedIds)
    const handleErrors = (errors: Record<string, string | string[]>) => {
      const fe: Record<string, string> = {}
      for (const [k, v] of Object.entries(errors)) {
        fe[k] = Array.isArray(v) ? v[0] : v
      }
      form.setError(fe)
    }

    router.post(
      `/inventory/bulk_location_move?asset_type=${assetType}`,
      { location_id: form.data.location_id, notes: form.data.notes, asset_ids: assetIds },
      {
        preserveState: true,
        onSuccess: () => {
          form.reset()
          onSuccess?.()
        },
        onError: handleErrors,
      }
    )
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(newOpen)
  }

  const assetCodes = allCodes.join(", ")
  const truncatedCodes = assetCodes.length > 50 ? assetCodes.substring(0, 50) + "..." : assetCodes

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t("inventory.moveLocation")}
            </DialogTitle>
            <DialogDescription>
              {t("inventory.movingAssets", { count: bulkCount })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground" title={assetCodes}>
              {truncatedCodes}
            </div>

            {hasErrors && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                  <div className="text-sm">
                    <ul className="space-y-1 text-destructive">
                      {errorMessages.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="move-location">{t("inventory.targetLocation")} *</Label>
              <SearchableSelect
                value={form.data.location_id}
                onChange={(value) => form.setData("location_id", value)}
                options={locations}
                placeholder={t("inventory.selectLocation")}
                allowEmpty={false}
                error={!!form.errors.location_id}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="move-location-notes">{t("inventory.form.notesLabel")}</Label>
              <Textarea
                id="move-location-notes"
                value={form.data.notes}
                onChange={(e) => form.setData("notes", e.target.value)}
                placeholder={t("inventory.form.notesPlaceholder")}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={form.processing || !form.data.location_id}>
              <MapPin className="h-4 w-4 mr-2" />
              {t("inventory.moveButton")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
